import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useAsync, useAsyncCallback } from 'react-async-hook';
import classNames from 'classnames';
import {
  GridActionsCellItem,
  GridActionsColDef,
  GridColumnHeaderParams,
  GridPagination,
} from '@mui/x-data-grid';
import { AxiosError } from 'axios';
import { UserRole } from '@upstart/harmony/lib/constants';
import closeImage from '../../../../assets/close.png';
import addCircleImage from '../../../../assets/add-circle.png';
import { DealerLaunchStepProps } from '../../types';
import { Button } from '../../../Input/Button/Button';
import { LinkButton } from '../../../Input/Button/LinkButton';
import { ErrorMessage } from '../../../ErrorMessage/ErrorMessage';
import { TooltipIcon } from '../../../TooltipIcon/TooltipIcon';
import {
  emailValidatorFunction,
  phoneValidator,
} from '../../../../utils/validation';
import { User } from '../../../../types/userTypes';
import UserService from '../../../../services/UserService';
import { useTypedSelector } from '../../../../redux/store';
import { addUsersDefaults } from '../../defaults';
import sharedStyles from '../../styles.module.scss';
import styles from './styles.module.scss';
import {
  DataGrid,
  mkCols,
  GridColDefWithValidation,
  validateAllRows,
  validateRow,
  resetRowErrorState,
} from '../../../DataGrid';
import ConfirmDeleteUser, { ConfirmDeleteUserProps } from './ConfirmDeleteUser';
import { useLaunchpadForm } from '../../../../hooks/useLaunchpadForm';
import {
  doUserCrud,
  isExistingUserRow,
  isNewUserRow,
  makeNewRow,
  postProcessCrud,
  rowHasUserData,
  RowModel,
  SimpleRole,
  SimpleRoleMapping,
} from './controller';
import { handleCsv, isParseError, sampleCsvUrl } from './csv';

const getSimpleRole = (user: Pick<User, 'roles'>) =>
  user.roles?.includes(UserRole.DESKING_MANAGER)
    ? SimpleRole.SALESMANAGER
    : SimpleRole.SALESPERSON;

export function makeRequiredFieldValidator(fieldName: string) {
  return (val: string) => {
    if (!val.trim().length) {
      return `${fieldName} is required`;
    }
    return '';
  };
}

// This is built into axios in newer versions than 0.20.0
function isAxiosError(e: any): e is AxiosError {
  return typeof e === 'object' && e.isAxiosError === true;
}

function getHeaderRenderer(tooltipContent: string) {
  return function renderHeader(
    params: GridColumnHeaderParams<any, RowModel, any>,
  ): React.ReactNode {
    return (
      <div>
        <span aria-label={params.colDef.headerName}>
          {params.colDef.headerName}
        </span>
        <TooltipIcon content={tooltipContent} />
      </div>
    );
  };
}

const gridDataColumns = mkCols<GridColDefWithValidation<RowModel>>(
  {
    field: 'firstName',
    headerName: 'First Name',
    width: 125,
    editable: true,
    sortable: false,
    validator: makeRequiredFieldValidator('First Name'),
  },
  {
    field: 'lastName',
    headerName: 'Last Name',
    width: 125,
    editable: true,
    sortable: false,
    validator: makeRequiredFieldValidator('Last Name'),
  },
  {
    field: 'email',
    headerName: 'Email',
    width: 200,
    editable: true,
    sortable: false,
    validator: emailValidatorFunction,
    renderHeader: getHeaderRenderer(
      'This email will also serve as the user login',
    ),
  },
  {
    field: 'phone',
    headerName: 'Phone',
    width: 150,
    editable: true,
    sortable: false,
    validator(val: RowModel['phone']) {
      // phone is not required, so no value is ok
      if (!val) {
        return '';
      }
      if (!phoneValidator.value.test(val)) {
        return phoneValidator.message;
      }
      return '';
    },
  },
  {
    field: 'crmUserId',
    headerName: 'CRM User ID',
    width: 150,
    editable: true,
    sortable: false,
    cellClassName: 'crmCell',
    renderHeader: getHeaderRenderer(
      'This is a unique identifier for each person in your CRM',
    ),
  },
  {
    field: 'roles',
    headerName: 'Role',
    width: 160,
    editable: true,
    sortable: false,
    type: 'singleSelect',
    valueGetter: ({ row }) => {
      // This allows the cell to be blank when a fresh row is loaded in
      if (!row.roles || row.roles.length === 0) {
        return '';
      }

      return getSimpleRole(row);
    },
    valueSetter: ({ row, value }) => ({
      ...row,
      roles: SimpleRoleMapping[value as SimpleRole],
    }),
    valueOptions: Object.values(SimpleRole),
    validator(val: RowModel['roles']) {
      if (!val || val.length === 0) {
        return 'Role is required.';
      }
      return '';
    },
    renderHeader: getHeaderRenderer('Sales managers get desking access'),
  },
);

export const DealerLaunchInstoreUsers: FunctionComponent<DealerLaunchStepProps> = ({
  dealer,
  prevStep,
  nextStep,
}) => {
  const authStore = useTypedSelector((state) => state.auth);

  // get the users for the dealer
  const getAllUsers = useAsync(UserService.getAll, [dealer.id]);
  const [rows, setRows] = useState<RowModel[]>([]);
  const [deletedRows, setDeletedRows] = useState<RowModel[]>([]);
  const [deleteModal, setDeleteModal] = useState<
    Pick<ConfirmDeleteUserProps, 'isOpen' | 'onConfirm' | 'user'>
  >({ isOpen: false, user: {}, onConfirm: () => {} });
  const [crudErrorMessages, setCrudErrorMessages] = useState<string[]>([]);
  const [csvErrorMessage, setCsvErrorMessage] = useState('');
  const csvUploader = useRef<HTMLInputElement>(null);

  const persistChanges = useAsyncCallback(doUserCrud);

  const columns = [
    ...gridDataColumns,
    ...mkCols<GridColDefWithValidation<RowModel>>({
      field: 'delete',
      headerName: '',
      width: 30,
      type: 'actions',
      getActions: ({ row: rowToDelete }) => [
        <GridActionsCellItem
          label="Remove User"
          icon={<img height={14} src={closeImage} alt="Delete" />}
          onClick={() => {
            const deleteRow = () => {
              setDeletedRows([...deletedRows, rowToDelete]);
              setRows(rows.filter((row) => row.id !== rowToDelete.id));
            };

            if (!rowHasUserData(rowToDelete)) {
              deleteRow();
              return;
            }

            setDeleteModal({
              isOpen: true,
              user: rowToDelete,
              onConfirm: deleteRow,
            });
          }}
        />,
      ],
    } as GridActionsColDef),
  ];

  useEffect(() => {
    getAllUsers.currentPromise?.then((allUsers) => {
      if (!allUsers) {
        return;
      }

      const gridRows: RowModel[] = [];
      allUsers.data.forEach((user) => {
        if (
          user.id === authStore.currentUser?.id ||
          user.roles?.includes(UserRole.ADMIN)
        ) {
          return;
        }

        gridRows.push({
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          phone: user.phone,
          crmUserId: user.crmUserId,
          roles: user.roles || [],
          id: user.email || Math.random().toString(),
          userId: user.id,
          isNew: false,
          isEdited: false,
          errors: {},
          blurredFields: new Set(),
        });
      });

      // pad the grid to a default of 12 rows
      while (gridRows.length < 12) {
        gridRows.push(makeNewRow());
      }

      setRows(gridRows);
    });
  }, [getAllUsers.currentPromise, authStore.currentUser]);

  // Use a form to alert the user when there are unsaved changes and they navigate away from the page
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { setValue, reset, register } = useLaunchpadForm<{
    sentinel: string;
  }>();
  useEffect(() => {
    if (hasUnsavedChanges) {
      setValue('sentinel', 'sen', { shouldDirty: true });
    } else {
      reset();
    }
  }, [hasUnsavedChanges, setValue, reset]);

  async function onContinueClicked() {
    // One alternative could be to validate rows in `processRowUpdate`, however it
    // seems that event fires later than this onClick. Validating here keeps things simpler.
    const allRowsValid = validateAllRows(rows, columns, rowHasUserData);
    setRows(rows.slice());
    if (!allRowsValid) {
      return;
    }

    const rv = await persistChanges.execute(rows, deletedRows, dealer.id);
    const newErrors: string[] = [];

    function pushErrorMessage(row: RowModel, err: any) {
      if (isNewUserRow(row)) {
        if (isAxiosError(err) && err.response?.status === 409) {
          newErrors.push(`User ${row.email} already exists`);
        } else {
          newErrors.push(`User ${row.email} could not be added`);
        }
      } else if (isExistingUserRow(row)) {
        newErrors.push(`User ${row.email} could not be updated`);
      } else {
        newErrors.push(`User ${row.email} could not be deleted`);
      }
    }

    const { updatedRows, updatedDeletedRows } = postProcessCrud(
      rows,
      deletedRows,
      rv,
      pushErrorMessage,
    );

    setRows(updatedRows);
    setDeletedRows(updatedDeletedRows);
    setCrudErrorMessages(newErrors);
    if (newErrors.length > 0) {
      setHasUnsavedChanges(true);
      return;
    }

    setHasUnsavedChanges(false);
    try {
      const updatedUsers = await getAllUsers.execute(dealer.id);
      nextStep(
        addUsersDefaults(
          { launchpad: { status: { finishedUsersConfig: true } } },
          updatedUsers.data || [],
        ),
      );
    } catch {
      setCrudErrorMessages(['Could not get users. Please try again.']);
    }
  }

  const GridFooter: React.FC = () => (
    <div className={styles.gridFooter}>
      <Button
        variant="minimal"
        onClick={() => {
          setRows([...rows, makeNewRow()]);
        }}
      >
        <img src={addCircleImage} alt="Add Row" className={styles.addRowIcon} />
        Add Row
      </Button>
      <GridPagination />
    </div>
  );

  const crudErrorMessageContent = () => {
    if (crudErrorMessages.length === 0) {
      return null;
    }
    return (
      <>
        There were problems updating users:
        <ul>
          {crudErrorMessages.map((err) => (
            <li key={err}>{err}</li>
          ))}
        </ul>
      </>
    );
  };

  return (
    <div
      className={classNames(
        sharedStyles.stepContainer,
        styles.usersStepContainer,
      )}
    >
      <ConfirmDeleteUser
        isOpen={deleteModal.isOpen}
        user={deleteModal.user}
        onConfirm={deleteModal.onConfirm}
        onClose={() =>
          setDeleteModal((currentDeleteModal) => ({
            ...currentDeleteModal,
            isOpen: false,
          }))
        }
      />
      <ErrorMessage>{crudErrorMessageContent()}</ErrorMessage>
      <ErrorMessage error={csvErrorMessage} />
      <form style={{ display: 'none' }}>
        <input type="text" name="sentinel" ref={register()} />
      </form>

      <h1 className={sharedStyles.stepTitle}>Add Users</h1>
      <p className={sharedStyles.stepSubtitle}>
        Add contact info to invite people to join your account on Upstart Auto
        Retail
      </p>

      <div className={classNames(sharedStyles.stepCard, styles.usersStepCard)}>
        <input
          type="file"
          ref={csvUploader}
          accept="text/csv"
          style={{ display: 'none' }}
          onChange={async (e) => {
            if (!e.target.files?.length) {
              return;
            }

            const result = await handleCsv(e.target.files[0]);
            if (isParseError(result)) {
              setCsvErrorMessage(result.parseError);
            } else {
              setRows(result);
              setHasUnsavedChanges(true);
              setCsvErrorMessage('');
            }

            // clear the input so that users can re-upload a file with the same name
            // eslint-disable-next-line no-param-reassign
            e.target.value = '';
          }}
        />
        <div className={styles.cardHeader}>
          <h1
            className={classNames(
              styles.usersStepHeading,
              styles.marginOverride,
            )}
          >
            User Management
          </h1>
          <Button
            label="+ Upload .CSV"
            size="small"
            className={styles.cardHeaderButton}
            onClick={() => csvUploader.current?.click()}
          />
          <LinkButton
            label="Download sample .CSV"
            variant="minimal"
            rawLink
            download="users-sample.csv"
            to={sampleCsvUrl}
          />
        </div>
        <p className={styles.cardSubtitle}>
          Note: any existing users shown in the table below will be hidden when
          you upload a new CSV file to add users. Don’t worry, any previous
          users will continue to have access to Upstart Auto Retail.
        </p>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={getAllUsers.loading}
          editMode="row"
          disableColumnMenu
          components={{
            Footer: GridFooter,
          }}
          processRowUpdate={(newRow) => {
            const updatedRow: typeof newRow = {
              ...newRow,
              isEdited: true,
            };

            if (rowHasUserData(updatedRow)) {
              validateRow(updatedRow, columns);
            } else {
              // if the row has become empty, consider it abandoned and clear all errors
              resetRowErrorState(updatedRow);
            }

            setRows(
              rows.map((row) => (row.id === newRow.id ? updatedRow : row)),
            );
            setHasUnsavedChanges(true);
            return updatedRow;
          }}
        />
      </div>

      <div className={sharedStyles.pageNav}>
        <Button
          className={sharedStyles.pageNavButton}
          label="Previous"
          onClick={prevStep}
        />

        <Button
          className={sharedStyles.pageNavButton}
          label="Continue"
          variant="primary"
          loading={persistChanges.loading || getAllUsers.loading}
          onClick={onContinueClicked}
        />
      </div>
    </div>
  );
};
